import { useCallback, useEffect, useState } from 'react';
import { getCashDeposits } from '../services';
import { ICashDeposit, ICashDepositsData, DepositStatus } from '../types';
import { resolveErrorMessage } from './hookUtils';

interface CashDepositsFilters {
	branch?: string;
	status?: DepositStatus;
}

interface CashDepositsState {
	deposits: ICashDeposit[];
	pagination: ICashDepositsData['pagination'] | null;
	loading: boolean;
	loadingMore: boolean;
	error: string | null;
	cachedAt: string | null;
	loadMore: () => void;
	hasMore: boolean;
	refetch: () => void;
}

export function useCashDeposits(filters: CashDepositsFilters = {}, pageSize = 25): CashDepositsState {
	const [deposits, setDeposits] = useState<ICashDeposit[]>([]);
	const [pagination, setPagination] = useState<ICashDepositsData['pagination'] | null>(null);
	const [loading, setLoading] = useState(true);
	const [loadingMore, setLoadingMore] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [cachedAt, setCachedAt] = useState<string | null>(null);
	const [offset, setOffset] = useState(0);
	const [tick, setTick] = useState(0);

	const { branch, status } = filters;

	useEffect(() => {
		let cancelled = false;
		const isFirstPage = offset === 0;
		if (isFirstPage) {
			setLoading(true);
			setError(null);
		} else {
			setLoadingMore(true);
		}

		getCashDeposits({ branch, status, limit: pageSize, offset })
			.then(({ data, cachedAt: newCachedAt }) => {
				if (cancelled) return;
				setDeposits((prev) => (isFirstPage ? data.deposits : [...prev, ...data.deposits]));
				setPagination(data.pagination);
				setCachedAt(newCachedAt);
				setLoading(false);
				setLoadingMore(false);
			})
			.catch((err) => {
				if (cancelled) return;
				setError(resolveErrorMessage(err));
				setLoading(false);
				setLoadingMore(false);
			});

		return () => {
			cancelled = true;
		};
	}, [branch, status, pageSize, offset, tick]);

	useEffect(() => {
		setDeposits([]);
		setPagination(null);
		setOffset(0);
	}, [branch, status]);

	const hasMore = pagination !== null && deposits.length < pagination.total;

	const loadMore = useCallback(() => {
		if (loading || loadingMore) return;
		setOffset((prev) => prev + pageSize);
	}, [loading, loadingMore, pageSize]);

	const refetch = useCallback(() => {
		setOffset(0);
		setTick((t) => t + 1);
	}, []);

	return { deposits, pagination, loading, loadingMore, error, cachedAt, loadMore, hasMore, refetch };
}
